import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'
import { ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { getVideoEmbedUrl, isGoogleDriveUrl } from '../shared/utils/videoHelpers'

export type VideoSource = 'youtube' | 'google_drive'

export interface VideoCardProps {
  /** Video title */
  title: string
  /** Video description */
  description: string
  /** Video URL (required for embedding) */
  videoUrl: string
  /** Video source type - 'youtube' or 'google_drive' */
  videoSource: VideoSource
  /** Video duration in MM:SS format */
  videoLength?: string
  /** Array of video authors */
  authors?: string[]
  /** Additional CSS classes */
  className?: string
  /** Layout variant - 'default' shows title above video, 'main' shows 60:40 layout */
  variant?: 'default' | 'main'
  /** Optional actions (e.g. "See all" button) rendered in the text column for variant="main" */
  actions?: React.ReactNode
}

/**
 * VideoCard Component
 *
 * A component for displaying embedded videos with metadata.
 * Used in the Expert Videos and Tutorial Videos pages to showcase video content.
 * Videos are embedded directly in the card using iframe (no button required).
 *
 * @param title - Video title
 * @param description - Video description
 * @param videoUrl - Video URL (required)
 * @param videoSource - Video source type ('youtube' or 'google_drive')
 * @param videoLength - Video duration in MM:SS format (optional)
 * @param authors - Array of video authors (optional)
 * @param className - Additional CSS classes
 */
const VideoCard: React.FC<VideoCardProps> = ({
  title,
  description,
  videoUrl,
  videoSource,
  videoLength,
  authors,
  className = '',
  variant = 'default',
  actions
}) => {
  // Determine if it's Google Drive
  const isGoogleDrive = useMemo(() => {
    return videoSource === 'google_drive' || isGoogleDriveUrl(videoUrl)
  }, [videoUrl, videoSource])

  // Get embed URL based on video source
  const embedUrl = useMemo(() => {
    return getVideoEmbedUrl(videoUrl, videoSource)
  }, [videoUrl, videoSource])

  // Render video embed (reusable for both variants)
  const renderVideoEmbed = () => {
    if (!embedUrl) return null

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.25%', // 16:9 aspect ratio
          height: 0,
          overflow: 'hidden',
          borderRadius: '8px',
          backgroundColor: INTERAX_COLORS.offBlack
        }}
      >
        {isGoogleDrive ? (
          <Box
            component="iframe"
            src={embedUrl}
            allow="autoplay; fullscreen"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={title}
          />
        ) : (
          <Box
            component="iframe"
            src={embedUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={title}
          />
        )}
      </Box>
    )
  }

  // Render video metadata (reusable for both variants)
  const renderVideoMetadata = () => {
    if (!videoLength && (!authors || authors.length === 0)) return null

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Duration - Only show if videoLength is provided and not empty */}
        {videoLength && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {videoLength}
            </Box>
          </Box>
        )}

        {/* Authors - Only show if authors array exists and has at least one item */}
        {authors && authors.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserGroupIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.thunderGrey }} />
            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {authors.join(', ')}
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  // Main variant: 60:40 layout (video : text). Hide length/author when description is present.
  const showMetadataInMain = variant !== 'main' || !description?.trim()
  if (variant === 'main') {
    return (
      <Box
        sx={{
          borderRadius: '8px',
          padding: '20px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '16px', md: '24px' },
          height: '100%'
        }}
        className={className}
      >
        {/* Left side: Video embed (60% on desktop) */}
        <Box
          sx={{
            width: { xs: '100%', md: '60%' },
            flexShrink: 0
          }}
        >
          {renderVideoEmbed()}
        </Box>

        {/* Right side: Title, description, optional metadata, actions (40% on desktop) */}
        <Box
          sx={{
            width: { xs: '100%', md: '40%' },
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            justifyContent: 'flex-start',
            paddingLeft: { xs: '0', md: '24px' },
            paddingRight: { xs: '0', md: '24px' }
          }}
        >
          {/* Video Title */}
          <Box className="text-headline-5" sx={{ color: INTERAX_COLORS.white }}>
            {title}
          </Box>

          {/* Video Description */}
          <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, flex: 1 }}>
            {description}
          </Box>

          {/* Video Metadata - hidden when description is non-empty for main variant */}
          {showMetadataInMain && renderVideoMetadata()}

          {/* Optional actions (e.g. See all button) */}
          <Box width={'100%'} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            {actions}
          </Box>
        </Box>
      </Box>
    )
  }

  // Default variant: Title above video, description below
  return (
    <Box
      sx={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        height: '100%'
      }}
      className={className}
    >
      {/* Video Title */}
      <Box className="text-headline-5" sx={{ color: INTERAX_COLORS.white }}>
        {title}
      </Box>

      {/* Embedded Video Player - 16:9 aspect ratio */}
      {renderVideoEmbed()}

      {/* Video Description */}
      <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, flex: 1 }}>
        {description}
      </Box>

      {/* Video Metadata - Only show if at least one field is available */}
      {renderVideoMetadata()}
    </Box>
  )
}

export default VideoCard
